import React, { useContext, useMemo, useState } from 'react';
import DashboardGadgetContext from '../contexts/DashboardGadgetContext';
import DashboardContextMenuProvider from './DashboardContextMenuProvider';
import useDashboardGadgetDataTypes from '../hooks/useDashboardGadgetDataTypes';
import FilterPlatesProvider from './FilterPlatesProvider';
import BonusPeriodGadgetGuard from '../components/BonusPeriodGadgetGuard';
import { DashboardFiltersContext } from './DashboardFiltersProvider';
import mergeDashboardFilters from './DashboardFiltersProvider/mergeDashboardFilters';

const DashboardGadgetProvider = ({
  children,
  dashboardGadget,
}: {
  children: JSX.Element | JSX.Element[];
  dashboardGadget: DashboardGadget;
}) => {
  const dataTypes = useDashboardGadgetDataTypes(dashboardGadget);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const dashboardFilters = useContext(DashboardFiltersContext);
  // The gadget's own filters, plus the dashboard's when it has a filter bar.
  // DashboardGadgetContext keeps the raw gadget, so editing the gadget never
  // saves the dashboard's filters into it.
  const { scope, drillDowns } = useMemo(
    () =>
      dashboardFilters
        ? mergeDashboardFilters({ dashboardFilters, gadget: dashboardGadget })
        : {
            scope: dashboardGadget.scope,
            drillDowns: dashboardGadget.drillDowns,
          },
    [dashboardFilters, dashboardGadget],
  );

  return (
    <DashboardGadgetContext.Provider
      value={{
        dashboardGadget,
        isEditing,
        setIsEditing,
      }}
    >
      <FilterPlatesProvider
        drillDowns={drillDowns}
        scope={scope}
        dataTypes={dataTypes}
        isDefaultFiltersDisabled={dashboardGadget.isDefaultFiltersDisabled}
      >
        <DashboardContextMenuProvider>
          <BonusPeriodGadgetGuard
            isBonusPeriodMode={!!dashboardGadget.isBonusPeriodMode}
          >
            {children}
          </BonusPeriodGadgetGuard>
        </DashboardContextMenuProvider>
      </FilterPlatesProvider>
    </DashboardGadgetContext.Provider>
  );
};

export default DashboardGadgetProvider;
